import { useRef, useState } from "react";

/*
 * Evernote's column divider: a thin hit area on a pane's edge; hovering or dragging shows an accent line.
 * While dragging, the size goes straight into a CSS variable on `.shell` (no re-render per move);
 * it's saved on release.
 */
export function Splitter({
  axis = "x",
  cssVar,
  min,
  max,
  onCommit,
}: {
  axis?: "x" | "y";
  cssVar: string;
  min: number;
  max: (container: DOMRect) => number;
  onCommit: (px: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ start: number; size: number; px: number } | null>(null);
  const [active, setActive] = useState(false);

  // The pane being sized is the element just before the splitter; its parent bounds the size.
  const pane = () => ref.current?.previousElementSibling as HTMLElement | null;
  const end = () => {
    const d = drag.current;
    drag.current = null;
    setActive(false);
    document.body.classList.remove("resizing-x", "resizing-y");
    if (d && d.px !== d.size) onCommit(d.px);
  };
  const set = (px: number) => (ref.current?.closest(".shell") as HTMLElement | null)?.style.setProperty(cssVar, `${px}px`);

  return (
    <div className={`pane-split ${axis}${active ? " on" : ""}`} ref={ref}>
      <div
        className="pane-split-hit"
        role="separator"
        aria-orientation={axis === "x" ? "vertical" : "horizontal"}
        onPointerDown={(e) => {
          const p = pane();
          if (!p || e.button !== 0) return;
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          const r = p.getBoundingClientRect();
          const size = axis === "x" ? r.width : r.height;
          drag.current = { start: axis === "x" ? e.clientX : e.clientY, size, px: size };
          setActive(true);
          document.body.classList.add(axis === "x" ? "resizing-x" : "resizing-y");
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          const p = pane();
          if (!d || !p?.parentElement) return;
          const box = p.parentElement.getBoundingClientRect();
          const delta = (axis === "x" ? e.clientX : e.clientY) - d.start;
          d.px = Math.round(Math.max(min, Math.min(max(box), d.size + delta)));
          set(d.px);
        }}
        onPointerUp={end}
        onPointerCancel={end}
      />
    </div>
  );
}
